import { reportLog } from './clientLogger'
import { createStackTrace } from './stackTraceUtils'
import printf from 'printf'
import { TrackJS } from 'trackjs'
import { ClientLogSeverity } from '@/logging/ClientLogBody'
import { isError, isNil, isString } from '@/helpers/helpers'

type ConsoleLogMethod = 'log' | 'error' | 'warn' | 'debug' | 'info'

const consoleMethodToSeverity: { [key in ConsoleLogMethod]: ClientLogSeverity } = {
  log: 'info',
  error: 'error',
  warn: 'warn',
  debug: 'debug',
  info: 'info'
}

const getConsoleMethodFromSeverity = (severity: ClientLogSeverity): ConsoleLogMethod => {
  if (severity === 'info') {
    return 'log'
  }
  if (severity === 'trace') {
    return 'debug'
  }
  return severity as ConsoleLogMethod
}

const formatConsoleArguments = (firstArgument?, ...additionalArgs): string => {
  if (isString(firstArgument) && firstArgument.includes('%') && additionalArgs.length > 0) {
    return printf(firstArgument, ...additionalArgs)
  } else if (isError(firstArgument)) {
    return firstArgument.message
  } else {
    return [firstArgument, ...additionalArgs].join(' ')
  }
}

export const hijackWindowConsole = (): Pick<Console, 'log' | 'error' | 'warn' | 'debug'> & { info: Console['log'] } => {
  const { log, error, warn, debug, info } = window.console
  const hijackedMethods = { log, error, warn, debug, info }
  // store window console's original methods, in case we want to also log to browser console

  const createLoggingHijacker =
    (logMethod: ConsoleLogMethod) =>
    (...args) => {
      const message = formatConsoleArguments(...args)
      const severity: ClientLogSeverity = consoleMethodToSeverity[logMethod]
      const updatedLogMethod = getConsoleMethodFromSeverity(severity)
      const stack = isError(args[0]) && !isNil(args[0].stack) ? args[0].stack : createStackTrace(1)
      reportLog(
        {
          message,
          stack_trace: stack,
          severity
        },
        args
      )
      TrackJS.isInstalled() && TrackJS.console[updatedLogMethod](...args)
    }

  // hijack window console methods
  window.console.log = createLoggingHijacker('info')
  for (const logMethod of ['info', 'error', 'debug', 'warn']) {
    window.console[logMethod] = createLoggingHijacker(logMethod as ConsoleLogMethod)
  }
  return hijackedMethods
}
